import {
  Alert,
  AlertIcon,
  Box,
  Flex,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import { useEffect, useMemo } from "react"
import {
  type TechnologyListResponseDTO,
  type TechnologyResponseDTO,
  TechnologyService,
} from "../../../client"
import { PaginationFooter } from "../../Common/PaginationFooter"
import {
  RowCheckbox,
  SelectAllCheckbox,
  useRowSelection,
} from "../selection.tsx"

type Props = {
  page: number
  pageSize: number
  searchQuery?: string
  statusFilter: "active" | "inactive" | "archived" | "all"
  onChangePage: (page: number) => void
  onSelectionChange?: (
    selectedItems: TechnologyResponseDTO[],
    selectedIds: string[],
  ) => void
}

export default function TechnologyList({
  page,
  pageSize,
  searchQuery,
  statusFilter,
  onChangePage,
  onSelectionChange,
}: Props) {
  const navigate = useNavigate()
  const hoverBg = useColorModeValue("gray.50", "whiteAlpha.100")
  const zebraBg = useColorModeValue("blackAlpha.50", "whiteAlpha.50")
  const cardBg = useColorModeValue("white", "gray.800")
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.200")
  const theadBg = useColorModeValue("gray.100", "whiteAlpha.100")
  const mutedText = useColorModeValue("gray.500", "gray.400")

  const { data, isPending, isError, error } =
    useQuery<TechnologyListResponseDTO>({
      queryKey: [
        "technologies",
        "list",
        searchQuery ?? "",
        statusFilter,
        page,
        pageSize,
      ],
      queryFn: () =>
        TechnologyService.getTechnologyListPage(
          searchQuery,
          statusFilter,
          page,
          pageSize,
        ),
    })

  const selection = useRowSelection()

  useEffect(() => {
    if (!onSelectionChange) return
    const selectedIds = selection.selectedIds
    const selectedItems: TechnologyResponseDTO[] = (data?.items ?? []).filter(
      (i) => selection.selected.has(i.id),
    )
    try {
      onSelectionChange(selectedItems, selectedIds)
    } catch (_) {}
  }, [selection.selected, selection.selectedIds, data, onSelectionChange])

  const items = data?.items ?? []
  const total = data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  const visibleIds = useMemo(() => items.map((i) => i.id), [items])
  const fmt = (d?: string) => (d ? new Date(d).toLocaleString() : "—")

  return (
    <Box
      bg={cardBg}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="xl"
      overflow="hidden"
      display="flex"
      flexDirection="column"
      h="full"
      minH={0}
    >
      {isPending && (
        <Flex justify="center" align="center" w="full" h="44vh">
          <Spinner size="xl" color="main.accent" />
        </Flex>
      )}

      {isError && !isPending && (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          Nie udało się pobrać technologii: {(error as any)?.message ?? "Błąd"}
        </Alert>
      )}

      {!isPending && !isError && items.length === 0 && (
        <Flex justify="center" align="center" w="full" h="36vh">
          <Text color={mutedText}>Brak technologii</Text>
        </Flex>
      )}

      {!isPending &&
        !isError &&
        items.length > 0 &&
        visibleIds.length === 0 && (
          <Flex justify="center" align="center" w="full" h="36vh" px={4}>
            <Text color={mutedText} textAlign="center">
              Brak wyników dla frazy: <b>{searchQuery?.trim()}</b>
            </Text>
          </Flex>
        )}

      {!isPending && !isError && visibleIds.length > 0 && (
        <TableContainer flex="1" minH={0} overflowY="auto">
          <Table size="sm" variant="simple" sx={{ tableLayout: "fixed" }}>
            <Thead bg={theadBg} position="sticky" top={0} zIndex={1}>
              <Tr>
                <Th w="40px" p={2}>
                  <SelectAllCheckbox
                    visibleIds={visibleIds}
                    selection={selection}
                  />
                </Th>
                <Th w={{ base: "200px", md: "280px" }}>Kod</Th>
                <Th w={{ base: "220px", md: "320px" }}>Nazwa</Th>
                <Th w="90px">Wersja</Th>
                <Th w="120px">Status</Th>
                <Th w="100px">Wejścia</Th>
                <Th w="100px">Wyjścia</Th>
                <Th w={{ base: "180px", md: "220px" }}>Utworzono</Th>
              </Tr>
            </Thead>
            <Tbody>
              {items.map((t, idx) => (
                <Tr
                  key={t.id}
                  bg={idx % 2 === 0 ? "transparent" : zebraBg}
                  _hover={{ bg: hoverBg }}
                  cursor="pointer"
                  onClick={() =>
                    navigate({
                      to: "/WMS/technologies/$technology",
                      params: { technology: t.id },
                    })
                  }
                >
                  <Td w="40px" p={2} onClick={(e) => e.stopPropagation()}>
                    <RowCheckbox id={t.id} selection={selection} />
                  </Td>
                  <Td
                    fontWeight="semibold"
                    whiteSpace="nowrap"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    title={(t.code ?? "").toLocaleUpperCase("pl-PL")}
                  >
                    {(t.code ?? "").toLocaleUpperCase("pl-PL")}
                  </Td>
                  <Td
                    whiteSpace="nowrap"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    title={t.name ?? ""}
                  >
                    {t.name}
                  </Td>
                  <Td color={mutedText} whiteSpace="nowrap">
                    {t.version}
                  </Td>
                  <Td>
                    {t.is_archived
                      ? "Archiwalna"
                      : t.is_active
                        ? "Aktywna"
                        : "Nieaktywna"}
                  </Td>
                  <Td whiteSpace="nowrap">{t.inputs?.length ?? 0}</Td>
                  <Td whiteSpace="nowrap">{t.outputs?.length ?? 0}</Td>
                  <Td whiteSpace="nowrap">{fmt(t.created_at)}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      )}

      {!isPending && !isError && total > 0 && (
        <Flex
          justify="space-between"
          align={{ base: "stretch", md: "center" }}
          direction={{ base: "column", md: "row" }}
          gap={3}
          px={4}
          py={4}
          flexShrink={0}
        >
          <Text fontSize="sm" color={mutedText}>
            Pokazano {(page - 1) * pageSize + 1}-
            {(page - 1) * pageSize + items.length} z {total}
          </Text>
          <PaginationFooter
            mt={0}
            onChangePage={onChangePage}
            page={page}
            hasNextPage={page < totalPages}
            hasPreviousPage={page > 1}
          />
        </Flex>
      )}
    </Box>
  )
}
